import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputFieldComponent, TextareaFieldComponent } from '../form';
import { INoteBodyPart } from './model/body-part.model';
import { BodyPartsService } from './service/body-part.service';

@UntilDestroy()
@Component({
  selector: 'siat-body-map',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    InputFieldComponent,
    TextareaFieldComponent,
  ],
  templateUrl: './body-map.component.html',
  styles: [
    `
      :host ::ng-deep .mat-expansion-panel {
        box-shadow: none !important;
      }
      :host ::ng-deep .mat-expansion-panel-header {
        padding: 0 16px;
      }

      /* Keyboard Focus Accessibility Style */
      /* This ensures the blue border appears ONLY during keyboard navigation (TAB) */
      /* It does not interfere with the mouse hover colors handled by JS */
      path:focus-visible {
        outline: none;
        stroke: #3b82f6 !important; /* Electric Blue */
        stroke-width: 3px !important;
        z-index: 50;
        paint-order: stroke;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: BodyMapComponent,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: BodyMapComponent,
      multi: true,
    },
  ],
})
export class BodyMapComponent implements ControlValueAccessor {
  /**
   * Service to retrieve body parts definition and SVG paths.
   */
  private readonly bodyPartsService = inject(BodyPartsService);

  /**
   * Angular FormBuilder to manage the reactive form.
   */
  private readonly fb = inject(FormBuilder);

  /**
   * List of anterior body parts (filtered from service).
   */
  readonly antBodyParts = this.bodyPartsService
    .getBodyParts()
    .filter(p => p.face === 'ant');

  /**
   * List of posterior body parts (filtered from service).
   */
  readonly postBodyParts = this.bodyPartsService
    .getBodyParts()
    .filter(p => p.face === 'post');

  /**
   * Signal holding the list of notes (The Form Value).
   */
  readonly notes = signal<INoteBodyPart[]>([]);

  /**
   * Signal tracking which accordion panel is currently expanded (editing).
   */
  readonly expandedPartId = signal<number | null>(null);

  /**
   * Signal tracking the ID of the part currently hovered by the mouse.
   */
  readonly hovered = signal<number | null>(null);

  /**
   * The shared FormGroup used for editing the active note.
   */
  formGroup: FormGroup;

  /**
   * Function to call when the value changes.
   */
  onChange: (value: INoteBodyPart[]) => void = () => {};

  /**
   * Function to call when the component is touched.
   */
  onTouched: () => void = () => {};

  constructor() {
    // 1. Initialize the form with required validators
    this.formGroup = this.fb.group({
      subject: ['', Validators.required],
      content: ['', Validators.required],
    });

    // 2. Listen to form changes to update the list in real-time
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(val => {
      const activeId = this.expandedPartId();
      if (activeId !== null) {
        this.updateNoteInList(activeId, val);
      }
    });
  }

  /**
   * Handles click on a body part in the SVG.
   * - If note exists: Expands the accordion for that note.
   * - If new: Creates a new note and expands the accordion.
   */
  handlePartClick(bodyPartId: number): void {
    this.onTouched();

    const existingNote = this.notes().find(n => n.bodyPartId === bodyPartId);

    if (!existingNote) {
      const newNote: INoteBodyPart = { bodyPartId, subject: '', content: '' };
      this.notes.update(list => [...list, newNote]);

      this.onChange(this.notes());
    }

    this.setActiveNote(bodyPartId);
  }

  /**
   * Sets the active note ID, expands the panel, and populates the form.
   */
  setActiveNote(bodyPartId: number): void {
    const note = this.notes().find(n => n.bodyPartId === bodyPartId);
    if (note) {
      this.expandedPartId.set(bodyPartId);

      this.formGroup.setValue(
        {
          subject: note.subject || '',
          content: note.content || '',
        },
        { emitEvent: false }
      );
    }
  }

  /**
   * Updates the specific note in the list when the form value changes.
   */
  private updateNoteInList(
    bodyPartId: number,
    formValue: { subject: string | null; content: string | null }
  ): void {
    const updatedList = this.notes().map(note => {
      if (note.bodyPartId === bodyPartId) {
        return {
          ...note,
          subject: formValue.subject,
          content: formValue.content,
        };
      }
      return note;
    });

    this.notes.set(updatedList);
    this.onChange(updatedList);
  }

  /**
   * Removes a note from the list and resets the form if necessary.
   */
  removeNote(bodyPartId: number, event: Event): void {
    event.stopPropagation();

    const filtered = this.notes().filter(n => n.bodyPartId !== bodyPartId);
    this.notes.set(filtered);
    this.onChange(filtered);

    if (this.expandedPartId() === bodyPartId) {
      this.expandedPartId.set(null);
      this.formGroup.reset({}, { emitEvent: false });
    }
  }

  /**
   * Sets the hovered signal when mouse enters a part.
   */
  handleMouseEnter(id: number): void {
    this.hovered.set(id);
  }

  /**
   * Clears the hovered signal when mouse leaves.
   */
  handleMouseLeave(): void {
    this.hovered.set(null);
  }

  /**
   * Calculates the SVG style (fill, stroke) dynamically based on state.
   * Priority: Selected > Hover > Default.
   */
  getStyle(id: number): { [key: string]: string } {
    const isSelected = this.notes().some(n => n.bodyPartId === id);
    const isHovered = this.hovered() === id;
    const isExpanded = this.expandedPartId() === id;

    let fill = 'rgb(75, 75, 77)';

    if (isSelected) {
      fill = 'rgb(192 39 31 / 82%)';
    } else if (isHovered) {
      fill = 'rgb(120, 120, 120)';
    }

    let stroke = 'white';
    let strokeWidth = '1px';

    if (isExpanded) {
      stroke = 'rgb(192 39 31)';
      strokeWidth = '2px';
    } else if (isSelected) {
      stroke = 'white';
      strokeWidth = '1px';
    }

    return {
      cursor: 'pointer',
      fill: fill,
      stroke: stroke,
      'stroke-width': strokeWidth,
      transition: 'fill 0.2s ease, stroke 0.2s ease',
    };
  }

  /**
   * Writes a new value to the element (from parent form).
   */
  writeValue(value: INoteBodyPart[]): void {
    this.notes.set(value || []);
  }

  /**
   * Registers a callback function that is called when the control's value changes in the UI.
   */
  registerOnChange(fn: (value: INoteBodyPart[]) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that is called by the forms API on initialization to update the form control on 'blur'.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Helper to get the human-readable name of a body part.
   */
  getPartName(id: number): string {
    return this.bodyPartsService.getPartById(id)?.name || 'Parte';
  }

  /**
   * TrackBy function for efficient DOM updates in loops.
   */
  trackById(index: number, item: INoteBodyPart): number {
    return item.bodyPartId || index;
  }

  /**
   * Questo metodo viene chiamato automaticamente da Angular ogni volta
   * che il valore cambia (onChange viene invocato).
   */
  validate(_control: AbstractControl): ValidationErrors | null {
    const currentNotes = this.notes();
    if (!currentNotes || currentNotes.length === 0) {
      return null;
    }
    const hasInvalidNote = currentNotes.some(
      note => !note.subject?.trim() || !note.content?.trim()
    );

    if (hasInvalidNote) {
      return {
        incompleteBodyPart: true, // Nome dell'errore a piacere
      };
    }

    return null;
  }

  /**
   * @returns { boolean }
   */
  isNoteInvalid(note: INoteBodyPart): boolean {
    return !note.subject?.trim() || !note.content?.trim();
  }
}
